// assets/site-header.js
import { Component } from '@theme/component';
import { lockScroll, unlockScroll, isClickedOutside } from '@theme/utilities';

/**
 * @typedef {object} SiteHeaderRefs
 * @property {HTMLButtonElement} hamburger
 * @property {HTMLElement} mobilePanel
 * @property {HTMLButtonElement} [mobileClose]
 * @property {HTMLButtonElement} [productsTrigger]
 * @property {HTMLElement} [productsPanel]
 * @property {HTMLElement} [productsNavItem]
 * @property {HTMLAnchorElement[]} [productsMenuItem]
 * @property {HTMLButtonElement} [mobileProductsTrigger]
 * @property {HTMLElement} [mobileProductsPanel]
 */

/**
 * Site header: scroll shadow, the "Products" hover/click dropdown, and the
 * mobile slide-in panel with its own accordion for Products.
 * @extends {Component<SiteHeaderRefs>}
 */
class SiteHeader extends Component {
  requiredRefs = ['hamburger', 'mobilePanel'];

  connectedCallback() {
    super.connectedCallback();
    this.#handleScroll();
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    document.addEventListener('click', this.#handleOutsideClick);
    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#handleScroll);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleKeydown);
    unlockScroll(this.refs.mobilePanel);
  }

  #handleScroll = () => {
    this.classList.toggle('site-header--scrolled', window.scrollY > 4);
  };

  /**
   * True on devices with a real hover-capable pointer (mouse/trackpad).
   * Touch taps synthesize a pointerenter immediately before click, so hover-driven
   * open/close must be skipped on touch or a tap would open then instantly close it.
   */
  static #hoverCapable = matchMedia('(hover: hover)');

  /** Opens the Products dropdown on hover — no-op on touch devices. */
  handleProductsPointerEnter() {
    if (!SiteHeader.#hoverCapable.matches) return;
    this.activateProducts();
  }

  /** Closes the Products dropdown on hover-out — no-op on touch devices. */
  handleProductsPointerLeave() {
    if (!SiteHeader.#hoverCapable.matches) return;
    this.deactivateProducts();
  }

  /** Opens the Products dropdown (hover or click/tap). */
  activateProducts() {
    const { productsTrigger, productsPanel } = this.refs;
    if (!productsTrigger || !productsPanel) return;
    productsTrigger.setAttribute('aria-expanded', 'true');
    productsPanel.hidden = false;
  }

  /** Closes the Products dropdown. */
  deactivateProducts() {
    const { productsTrigger, productsPanel } = this.refs;
    if (!productsTrigger || !productsPanel) return;
    if (productsTrigger.getAttribute('aria-expanded') !== 'true') return;
    productsTrigger.setAttribute('aria-expanded', 'false');
    productsPanel.hidden = true;
  }

  /** Click/tap toggle for the Products trigger (touch devices have no hover). */
  toggleProducts() {
    const isOpen = this.refs.productsTrigger?.getAttribute('aria-expanded') === 'true';
    isOpen ? this.deactivateProducts() : this.activateProducts();
  }

  /**
   * Closes the dropdown once focus leaves the trigger, panel, and everything
   * between them — keeps it open while tabbing through its links.
   * @param {FocusEvent} event
   */
  handleProductsBlur(event) {
    const { productsNavItem } = this.refs;
    if (!productsNavItem) return;
    if (event.relatedTarget instanceof Node && productsNavItem.contains(event.relatedTarget)) return;
    this.deactivateProducts();
  }

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    const { productsTrigger, productsPanel } = this.refs;
    if (
      productsTrigger?.getAttribute('aria-expanded') === 'true' &&
      productsPanel &&
      isClickedOutside(event, productsPanel) &&
      isClickedOutside(event, productsTrigger)
    ) {
      this.deactivateProducts();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const productsOpen = this.refs.productsTrigger?.getAttribute('aria-expanded') === 'true';

    if (event.key === 'Escape') {
      if (productsOpen) {
        this.deactivateProducts();
        this.refs.productsTrigger?.focus();
      }
      if (!this.refs.mobilePanel.hasAttribute('inert')) {
        this.closeMobilePanel();
      }
      return;
    }

    if (!productsOpen || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;

    const items = this.refs.productsMenuItem ?? [];
    if (!items.length) return;

    event.preventDefault();
    const currentIndex = items.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex =
      currentIndex === -1 ? (direction === 1 ? 0 : items.length - 1) : (currentIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  openMobilePanel() {
    const { mobilePanel, hamburger, mobileClose } = this.refs;
    mobilePanel.removeAttribute('inert');
    hamburger.setAttribute('aria-expanded', 'true');
    lockScroll(mobilePanel);
    mobileClose?.focus();
  }

  closeMobilePanel() {
    const { mobilePanel, hamburger } = this.refs;
    mobilePanel.setAttribute('inert', '');
    hamburger.setAttribute('aria-expanded', 'false');
    unlockScroll(mobilePanel);
    hamburger.focus();
  }

  toggleMobileProducts() {
    const { mobileProductsTrigger, mobileProductsPanel } = this.refs;
    if (!mobileProductsTrigger || !mobileProductsPanel) return;
    const expanded = mobileProductsTrigger.getAttribute('aria-expanded') === 'true';
    mobileProductsTrigger.setAttribute('aria-expanded', String(!expanded));
    mobileProductsPanel.hidden = expanded;
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}
